import React, { createContext, useContext, useMemo } from "react";
import { useSelector } from "react-redux";
import _ from "lodash";

const FilteredDetectionsContext = createContext(null);

const detectionsDataSelector = (state) => state.railInspection.detections.data;
const detectionsReviewFiltersSelector = (state) => state.railInspection.detections.reviewFilters;
const detectionsTypeFiltersSelector = (state) => state.railInspection.detections.typeFilters;
const detectionConditionFiltersSelector = (state) => state.railInspection.detections.detectionConditionFilters || {};
const dipAngleFilterSelector = (state) => state.railInspection.dipAngleFilter;
const gapSizeFilterSelector = (state) => state.railInspection.gapSizeFilter;
const defectSizeFilterSelector = (state) => state.railInspection.defectSizeFilter;
const adjustmentSwitchFiltersSelector = (state) => state.railInspection.adjustmentSwitchFilters;

const filterDetectionType = (detection, typeFilters) => {
    if (typeFilters && !typeFilters.includes(detection.name)) {
        return false;
    }
    return true;
};

const filterDetectionReviewStatus = (detection, reviewFilters) => {
    if (reviewFilters.length && !reviewFilters.includes(detection.review_status)) {
        return false;
    }
    return true;
};

const filterDetectionCondition = (detection, conditionFilter) => {
    if (conditionFilter && conditionFilter[detection.name]) {
        let filter = false;
        _.forEach(Object.keys(conditionFilter[detection.name]), (condition) => {
            let markerCondition = _.get(detection, ["conditions", condition, "user"], null);
            if (!markerCondition) {
                markerCondition = _.get(detection, ["conditions", condition, "auto"], null);
            }

            if (!markerCondition && detection.name === "Joint" && condition === "dip_angle") {
                if (detection.analysis.dip_angle_valid && detection.analysis.dip_angle_mrad > 0) {
                    markerCondition = "dip_angle_present";
                }

                if (detection.analysis.dip_angle_valid && detection.analysis.dip_angle_mrad === 0) {
                    markerCondition = "no_dip_angle";
                }
            }

            if (!markerCondition && detection.name === "Joint" && condition === "gap_mm") {
                if (_.get(detection, "analysis.gap_mm") !== undefined) {
                    markerCondition = "gap_data";
                }
            }

            if (!markerCondition) {
                markerCondition = "none";
            }
            if (!conditionFilter[detection.name][condition].includes(markerCondition)) {
                filter = true;
            }
        });

        if (filter) {
            return false;
        }
    }

    return true;
};

const filterDetectionDipAngle = (detection, dipAngleFilter) => {
    if (detection.name === "Joint" && dipAngleFilter && dipAngleFilter.length) {
        let dipAngle = _.get(detection, ["analysis", "dip_angle_mrad"], 0);
        if (!_.get(detection, ["analysis", "dip_angle_valid"], false)) {
            dipAngle = 0;
        }
        if (dipAngle < dipAngleFilter[0] || dipAngle > dipAngleFilter[1]) {
            return false;
        }
    }
    return true;
};

const filterDetectionGapSize = (detection, gapSizeFilter) => {
    if (detection.name === "Joint" && gapSizeFilter && gapSizeFilter.length) {
        // this just means there is a gap size filter range set, even if 0-max
        let gap_size = _.get(detection, ["analysis", "gap_mm"], 0);

        if (gap_size < gapSizeFilter[0] || gap_size > gapSizeFilter[1]) {
            return false;
        }
    }
    return true;
};

const filterDetectionDefectSize = (detection, defectSizeFilter) => {
    if (detection.name === "Defect" && defectSizeFilter?.length) {
        const defectSize = detection.detections.reduce((sum, d) => (sum += d.bbox[3]), 0);

        return defectSize >= defectSizeFilter[0] && defectSize <= defectSizeFilter[1];
    }

    return true;
};

const filterDetectionAdjustmentSwitch = (detection, adjustmentSwitchFilters) => {
    if (detection.name === "Adjustment Switch Base Plate" && adjustmentSwitchFilters) {
        const measurements = _.get(detection, "measurements", {});
        let result = true;
        _.forEach(Object.entries(adjustmentSwitchFilters), ([filterType, filter]) => {
            if (filterType === "tip" && filter && filter.length) {
                if (
                    (measurements.tip_offset_1 < filter[0] || measurements.tip_offset_1 > filter[1]) &&
                    (measurements.tip_offset_2 < filter[0] || measurements.tip_offset_2 > filter[1])
                ) {
                    result = false;
                    return;
                }
            }
            if (filterType === "gap" && filter && filter.length) {
                if (
                    (measurements.cut_out_1 < filter[0] || measurements.cut_out_1 > filter[1]) &&
                    (measurements.cut_out_2 < filter[0] || measurements.cut_out_2 > filter[1])
                ) {
                    result = false;
                    return;
                }
            }
            if (filterType === "heel" && filter && filter.length) {
                if (
                    (measurements.heel_offset_1 < filter[0] || measurements.heel_offset_1 > filter[1]) &&
                    (measurements.heel_offset_2 < filter[0] || measurements.heel_offset_2 > filter[1])
                ) {
                    result = false;
                    return;
                }
            }
        });

        if (!result) {
            return result;
        }
    }
    return true;
};

const filterInspectionMetadata = (detection, metadataFilters) => {
    const conditionFilters = metadataFilters.conditions;
    const dipAngleFilters = metadataFilters.dipAngle;
    const gapSizeFilters = metadataFilters.gapSize;
    const defectSizeFilters = metadataFilters.defectSize;
    const adjustmentSwitchFilters = metadataFilters.adjustmentSwitch;

    if (!filterDetectionCondition(detection, conditionFilters)) {
        return false;
    }

    if (!filterDetectionDipAngle(detection, dipAngleFilters)) {
        return false;
    }

    if (!filterDetectionGapSize(detection, gapSizeFilters)) {
        return false;
    }

    if (!filterDetectionDefectSize(detection, defectSizeFilters)) {
        return false;
    }

    if (!filterDetectionAdjustmentSwitch(detection, adjustmentSwitchFilters)) {
        return false;
    }

    return true;
};

function filterInspectionObservations(observations, typeFilters, reviewFilters, metadataFilters) {
    const filtered = observations.filter((observation) => {
        if (!filterDetectionType(observation, typeFilters)) {
            return false;
        }

        if (!filterDetectionReviewStatus(observation, reviewFilters)) {
            return false;
        }

        if (!filterInspectionMetadata(observation, metadataFilters)) {
            return false;
        }

        return true;
    });

    return filtered;
}

export const FilteredDetectionsProvider = ({ children }) => {
    const detectionsData = useSelector(detectionsDataSelector);
    const detectionsReviewFilters = useSelector(detectionsReviewFiltersSelector);
    const detectionsTypeFilters = useSelector(detectionsTypeFiltersSelector);
    const detectionConditionFilters = useSelector(detectionConditionFiltersSelector);
    const dipAngleFilter = useSelector(dipAngleFilterSelector);
    const gapSizeFilter = useSelector(gapSizeFilterSelector);
    const defectSizeFilter = useSelector(defectSizeFilterSelector);
    const adjustmentSwitchFilters = useSelector(adjustmentSwitchFiltersSelector);

    const filteredDetections = useMemo(() => {
        let filtered = [];

        if (detectionsData && detectionsData.length && detectionsReviewFilters && detectionsTypeFilters && detectionConditionFilters) {
            const inspectionMetadataFilters = {
                conditions: detectionConditionFilters,
                dipAngle: dipAngleFilter,
                gapSize: gapSizeFilter,
                defectSize: defectSizeFilter,
                adjustmentSwitch: adjustmentSwitchFilters,
            };

            filtered = filterInspectionObservations(detectionsData, detectionsTypeFilters, detectionsReviewFilters, inspectionMetadataFilters);
        }

        return filtered;
    }, [
        detectionsData,
        detectionsReviewFilters,
        detectionsTypeFilters,
        detectionConditionFilters,
        dipAngleFilter,
        gapSizeFilter,
        defectSizeFilter,
        adjustmentSwitchFilters,
    ]);

    return <FilteredDetectionsContext.Provider value={filteredDetections}>{children}</FilteredDetectionsContext.Provider>;
};

export const useFilteredDetections = () => {
    const context = useContext(FilteredDetectionsContext);

    if (context === undefined) {
        throw new Error("useFilteredDetections must be used within a FilteredDetectionsProvider");
    }

    return context;
};
